import React, { useState, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, IconButton, TextField, Grid,
    Switch, InputAdornment, Typography, Box, MenuItem,
} from '@mui/material';
import currencyList from "../../../sampleData/currencyList";
import {Stack} from "@mui/system";


function ReceiptDialogEditCosts({ isOpen, onClose, receipt, onSave }) {
    const [amount, setAmount] = useState(0);
    const [businessAmount, setBusinessAmount] = useState(0);
    const [currency, setCurrency] = useState("USD");
    const [isBusiness, setIsBusiness] = useState(false);

    useEffect(() => {
        if (isOpen && receipt) {
            setAmount(parseFloat(receipt.amount || 0).toFixed(2));
            setBusinessAmount(parseFloat(receipt.business_expense_amount || 0).toFixed(2));
            setCurrency(receipt.currency || "USD");
            setIsBusiness(receipt.is_business_expense || false);
        }
    }, [isOpen, receipt]);

    const handleAmountChange = (e) => {
        const val = e.target.value;
        // Allow empty string so user can clear the field to type
        if (val === "") {
            setAmount("");
            return;
        }
        setAmount(val);
    };

    // UI Helper: Toggle business amount when the switch is flipped
    const handleToggleBusiness = (checked) => {
        setIsBusiness(checked);

        if (checked) {
            // Only auto-fill if the business amount is currently empty, 0, or hasn't been touched
            if (!businessAmount || parseFloat(businessAmount) === 0) {
                // Match the main amount and ensure it has the two decimal places
                const syncedAmount = parseFloat(amount || 0).toFixed(2);
                setBusinessAmount(syncedAmount);
            }
        } else {
            // Optional: Clear the business amount when toggled off,
            // or keep it in case they toggle back on. Usually, clearing is cleaner.
            setBusinessAmount("0.00");
        }
    };

    const handleSave = () => {
        onSave(receipt.id, {
            amount: parseFloat(amount),
            business_expense_amount: isBusiness ? parseFloat(businessAmount) : 0,
            currency: currency.toUpperCase(),
            is_business_expense: isBusiness
        });
        onClose();
    };

    return (
        <Dialog
            open={isOpen}
            onClose={onClose}
            PaperProps={{
                sx: {
                    borderRadius: '20px',
                    padding: '8px',
                    minWidth: 400
                }
            }}
        >
            {/* Creative Header */}
            <DialogTitle sx={{
                p: 3,
                textAlign: 'center',
                background: 'linear-gradient(135deg, #151269 0%, #352fcc 100%)',
                color: 'white',
                borderRadius: '15px 15px 5px 5px',
                position: 'relative'
            }}>
                <Typography variant="h6" sx={{ fontWeight: 700, letterSpacing: '1px' }}>
                    FINANCIAL DETAILS
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.8 }}>
                    Track your spending and deductions
                </Typography>
            </DialogTitle>

            <IconButton onClick={onClose} sx={{ position: 'absolute', right: 16, top: 16, color: 'white' }}>
                <i className="icon-cross" style={{ fontSize: '18px' }} />
            </IconButton>

            <DialogContent sx={{ mt: 3 }}>
                <Box container spacing={3}>
                    {/* Main Receipt Section */}
                    <Box item xs={12}>
                        <Box sx={{ p: 2, bgcolor: '#f8f9fa', borderRadius: '16px', border: '1px solid #e0e0e0' }}>
                            <Typography variant="overline" color="textSecondary" sx={{ fontWeight: 800, color: '#151269' }}>
                                Total Receipt Value
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mt: 0.5 }}>
                                <Box sx={{ flexGrow: 1 }}>
                                    <TextField
                                        fullWidth
                                        variant="standard"
                                        label="Amount Paid"
                                        type="number"
                                        value={amount}
                                        onChange={handleAmountChange}
                                        // 🎯 Formatting on blur ensures that "33.9" becomes "33.90" only when they finish typing
                                        onBlur={() => setAmount(parseFloat(amount || 0).toFixed(2))}
                                        InputProps={{
                                            startAdornment: <InputAdornment position="start" sx={{ fontWeight: 900, color: '#151269' }}>$</InputAdornment>,
                                            sx: { fontSize: '1.5rem', fontWeight: 800, color: '#151269' }
                                        }}
                                    />
                                </Box>
                                <Box sx={{ minWidth: '80px' }}>
                                    <TextField
                                        select
                                        fullWidth
                                        variant="standard"
                                        label="Currency"
                                        value={currency}
                                        onChange={(e) => setCurrency(e.target.value)}
                                        helperText={currencyList.find(c => c.code === currency)?.name}
                                        FormHelperTextProps={{
                                            sx: {
                                                fontWeight: 600,
                                                color: '#aaa',
                                                textAlign: 'right',
                                                whiteSpace: 'nowrap',
                                                marginRight: 0,
                                                width: '100%', // 🎯 Forces helper text to align to the grid edge
                                                display: 'block'
                                            }
                                        }}
                                        SelectProps={{
                                            renderValue: (selected) => selected,
                                            // 🎯 This ensures the clickable area spans the whole grid item
                                            sx: {
                                                width: '100%',
                                                '& .MuiSelect-select': {
                                                    textAlign: 'right',
                                                    paddingRight: '24px !important', // Leaves room for the arrow
                                                }
                                            },
                                            MenuProps: {
                                                PaperProps: {
                                                    sx: { borderRadius: '15px', mt: 1, boxShadow: '0 8px 24px rgba(0,0,0,0.15)', maxHeight: 400 }
                                                }
                                            }
                                        }}
                                        // 🎯 Styles the underlying Input component to span 100%
                                        sx={{
                                            width: '100%',
                                            '& .MuiInput-root': { width: '100%' }
                                        }}
                                        inputProps={{
                                            style: { textAlign: 'right', fontWeight: 800, color: '#151269', fontSize: '1.1rem' }
                                        }}
                                        InputLabelProps={{
                                            sx: {
                                                fontWeight: 700,
                                                color: '#151269',
                                                textAlign: 'right',
                                                width: '100%',
                                                transformOrigin: 'right' // 🎯 Ensures the label shrinks toward the right on focus
                                            }
                                        }}
                                    >
                                        {currencyList.map((c) => (
                                            <MenuItem key={c.code} value={c.code} sx={{ py: 1.5, px: 2 }}>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                                                    {/* Primary: The Code */}
                                                    <Typography sx={{ fontWeight: 800, color: '#151269', fontSize: '1rem' }}>
                                                        {c.code}
                                                    </Typography>

                                                    {/* Secondary: Name and Symbol (Obscured) */}
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                        <Typography variant="caption" sx={{ color: '#aaa', fontWeight: 500, fontSize: '0.75rem' }}>
                                                            {c.name}
                                                        </Typography>
                                                        <Typography
                                                            sx={{
                                                                color: '#151269',
                                                                opacity: 0.15,
                                                                fontWeight: 900,
                                                                fontSize: '1.1rem',
                                                                width: '20px',
                                                                textAlign: 'center'
                                                            }}
                                                        >
                                                            {c.symbol}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                </Box>
                            </Box>
                        </Box>
                    </Box>

                    {/* Business Logic Section */}
                    <Grid item xs={12}>
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            p: 1,
                            px: 2,
                            bgcolor: isBusiness ? 'rgba(21, 18, 105, 0.05)' : 'transparent',
                            borderRadius: '12px',
                            transition: '0.3s'
                        }}>
                            <Box display="flex" alignItems="center" gap={1.5}>
                                <Box sx={{
                                    width: 40, height: 40, borderRadius: '10px',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    bgcolor: isBusiness ? '#151269' : '#f0f0f0',
                                    color: isBusiness ? 'white' : '#ccc',
                                    transition: '0.3s'
                                }}>
                                    <i className="icon-briefcase" style={{ fontSize: '18px' }} />
                                </Box>
                                <Typography sx={{ fontWeight: 700, color: isBusiness ? '#151269' : '#666' }}>
                                    Business Expense
                                </Typography>
                            </Box>
                            <Switch
                                checked={isBusiness}
                                onChange={(e) => handleToggleBusiness(e.target.checked)}
                                sx={{
                                    '& .MuiSwitch-switchBase.Mui-checked': { color: '#151269' },
                                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#151269' },
                                }}
                            />
                        </Box>
                    </Grid>

                    {isBusiness && (
                        <Grid item xs={12}>
                            <Box sx={{
                                p: 2,
                                borderRadius: '16px',
                                border: '2px solid #151269',
                                bgcolor: 'white',
                                boxShadow: '0 4px 15px rgba(21, 18, 105, 0.1)',
                                animation: 'fadeIn 0.3s ease-in'
                            }}>
                                <Typography variant="overline" sx={{ color: '#151269', fontWeight: 900, display: 'block', mb: 1 }}>
                                    Deductible Business Amount
                                </Typography>
                                <TextField
                                    fullWidth
                                    variant="outlined"
                                    type="number"
                                    value={businessAmount}
                                    onChange={(e) => setBusinessAmount(e.target.value)}
                                    onBlur={() => setBusinessAmount(parseFloat(businessAmount || 0).toFixed(2))}
                                    // 🎯 Use the Intl.NumberFormat here for a "live" professional readout
                                    helperText={
                                        <Typography variant="caption" sx={{ fontWeight: 600, color: '#151269', mt: 0.5, display: 'block' }}>
                                            Total Business Portion: {new Intl.NumberFormat(undefined, {
                                            style: 'currency',
                                            currency: currency || 'USD',
                                            minimumFractionDigits: 2
                                        }).format(businessAmount || 0)}
                                        </Typography>
                                    }
                                    InputProps={{
                                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                        sx: { borderRadius: '12px', fontWeight: 700 }
                                    }}
                                />
                            </Box>
                        </Grid>
                    )}
                </Box>
            </DialogContent>

            <DialogActions sx={{ p: 3, justifyContent: 'space-between' }}>
                <Button onClick={onClose} sx={{ color: '#151269' }}>
                    Cancel
                </Button>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    sx={{
                        borderRadius: '10px',
                        px: 4,
                        bgcolor: '#151269',
                        '&:hover': { bgcolor: '#352fcc' }
                    }}
                >
                    Save Financials
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default ReceiptDialogEditCosts;